import { z } from 'zod';
import { IntakePlaybookReadySchema, IntakePlaybookUpdatedSchema } from '@merlin/project-schema/intake-playbook';

const text = (max = 32_000) => z.string().min(1).max(max);
const stringList = (maxItems = 100) => z.array(text()).max(maxItems);
const isoDateTime = z.string().datetime({ offset: true });
const httpUrl = z.string().url().refine((value) => {
  const protocol = new URL(value).protocol;
  return protocol === 'http:' || protocol === 'https:';
}, 'Expected an HTTP(S) URL');

const capturePayloadSchema = z.object({
  slot: z.enum([
    'phone',
    'window',
    'topic',
    'role',
    'current_state',
    'desired_state',
    'constraints',
    'success',
  ]),
  value: text(),
  status: z.enum(['tentative', 'confirmed']),
}).strict();

export const contactDetailsUpdatedSchema = z.object({
  revision: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  details: z.object({
    name: z.string().max(120).optional(),
    email: z.string().max(254).optional(),
    phone: z.string().max(40).optional(),
    organisation: z.string().max(160).optional(),
  }).strict(),
  confirmed: z.literal(false),
  reviewRequested: z.boolean(),
}).strict();

export type ContactDetailsUpdated = z.infer<typeof contactDetailsUpdatedSchema>;
export type ContactDetailsRejected = { revision: number; code: 'stale_revision' | 'invalid_details' };

const offerSchema = z.object({
  id: z.enum(['foundry', 'ai_forge', 'team-training']),
  name: text(200),
  summary: text(),
  format: text(),
  who: text(),
  deliverables: stringList(20),
}).strict();

const legacyDiagramNodeSchema = z.object({
  id: text(200),
  label: text(200),
  sub: text(500).optional(),
}).strict();

const legacyDiagramSchema = z.object({
  id: text(200),
  title: text(500),
  subtitle: text(1_000),
  nodes: z.array(legacyDiagramNodeSchema).min(1).max(20),
}).strict();

const workflowDiagramNodeSchema = z.object({
  label: z.string().min(2).max(60),
  sub: z.string().min(2).max(120).optional(),
}).strict();

const workflowColumnSchema = z.array(workflowDiagramNodeSchema).min(2).max(4);

export const workflowDiagramPayloadSchema = z.object({
  title: z.string().min(4).max(80),
  subtitle: z.string().min(4).max(160),
  pain: workflowColumnSchema,
  bridge: workflowColumnSchema,
  outcome: workflowColumnSchema,
}).strict();

const bookingPayloadSchema = z.object({
  reference: text(200),
  bookingUid: z.string().max(500),
  receivedAt: isoDateTime,
  bookingStartUtc: isoDateTime,
  bookingEndUtc: isoDateTime.optional(),
  sessionId: text(200),
  name: text(200),
  email: z.string().email().max(320),
  organisation: text(300),
  phone: text(100).optional(),
  callerTimezone: text(200),
  topic_summary: text(),
  briefing_summary: text(),
  location: text(500).optional(),
  cancelUrl: httpUrl.optional(),
  rescheduleUrl: httpUrl.optional(),
  failed: z.boolean().optional(),
}).strict();

const briefScenarioSchema = z.object({
  name: text(500),
  given: stringList(),
  when: stringList(),
  then: stringList(),
}).strict();

const emailDeliveryStatusSchema = z.enum(['accepted', 'not_sent', 'unknown']);

const briefSubmittedPayloadSchema = z.object({
  reference: text(200),
  caller: z.object({
    name: text(200),
    email: z.string().email().max(320),
    organisation: text(300).optional(),
    role: text(300).optional(),
  }).strict(),
  feature: z.object({
    title: text(500),
    narrative: text(),
  }).strict(),
  background: stringList(),
  scenarios: z.array(briefScenarioSchema).min(1).max(100),
  constraints: stringList(),
  success_criteria: stringList(),
  next_step: text(),
  open_questions: stringList().optional(),
  gherkin: text(),
  receivedAt: isoDateTime,
  emailDelivery: z.object({ team: emailDeliveryStatusSchema, caller: emailDeliveryStatusSchema }).strict().optional(),
}).strict();

const serverUiEventSchema = z.discriminatedUnion('kind', [
  z.object({ type: z.literal('event'), kind: z.literal('playbook_updated'), payload: IntakePlaybookUpdatedSchema }).strict(),
  z.object({ type: z.literal('event'), kind: z.literal('playbook_ready'), payload: IntakePlaybookReadySchema }).strict(),
  z.object({
    type: z.literal('event'), kind: z.literal('contact_details_confirmed'),
    payload: z.object({ revision: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER) }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'), kind: z.literal('contact_details_updated'), payload: contactDetailsUpdatedSchema,
  }).strict(),
  z.object({
    type: z.literal('event'), kind: z.literal('contact_details_rejected'),
    payload: z.object({
      revision: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
      code: z.enum(['stale_revision', 'invalid_details']),
    }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('booking'),
    payload: bookingPayloadSchema,
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('escalation'),
    payload: z.object({
      reason: text(),
      caller_summary: text(),
      kind: z.enum(['follow_up', 'human_help']).optional(),
      phone: text(100).optional(),
      emailDelivery: emailDeliveryStatusSchema.optional(),
    }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('info-email'),
    payload: z.object({
      receivedAt: isoDateTime,
      sessionId: text(200),
      email: z.string().email().max(320),
      topics: z.array(z.enum(['offers', 'example-builds', 'case-studies'])).min(1).max(3),
    }).strict(),
  }).strict(),
  z.object({ type: z.literal('event'), kind: z.literal('capture'), payload: capturePayloadSchema }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('show_offers'),
    payload: z.object({
      highlight: z.enum(['foundry', 'ai_forge', 'team-training']).nullable(),
      offers: z.array(offerSchema).min(1).max(10),
    }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('tool_start'),
    payload: z.object({ id: z.string().max(256), name: text(256) }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('tool_done'),
    payload: z.object({ id: z.string().max(256), name: text(256), ok: z.boolean() }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('context_tag'),
    payload: z.object({
      key: z.enum(['industry', 'team_size', 'offer', 'location']),
      value: text(500),
    }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('show_example_builds'),
    payload: z.object({
      highlight: z.string().max(200).nullable(),
      items: z.array(z.object({
        id: text(200),
        name: text(500),
        summary: text(),
        diagram: legacyDiagramSchema.nullable(),
      }).strict()).min(1).max(50),
    }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('show_contact'),
    payload: z.object({
      reason: z.string().max(1_000).nullable(),
      company: z.object({
        name: text(500),
        location: text(500),
        website: httpUrl,
        email: z.string().email().max(320),
        phone: text(100),
        positioning: text(),
      }).strict(),
    }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('show_savings'),
    payload: z.object({
      domain: text(100),
      hoursPerWeek: z.number().finite().nonnegative(),
      hoursSavedPerWeek: z.number().finite().nonnegative(),
      annualHours: z.number().finite().nonnegative(),
      annualSavings: z.number().finite().nonnegative(),
      rate: z.number().finite().nonnegative(),
      automationPct: z.number().finite().min(0).max(100),
    }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('workflow_diagram'),
    payload: workflowDiagramPayloadSchema,
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('show_briefing_book'),
    payload: z.object({ question: text(), answer: text() }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('brief_submitted'),
    payload: briefSubmittedPayloadSchema,
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('request_caller_details'),
    payload: z.object({}).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('booking_rescheduled'),
    payload: z.object({
      sessionId: text(200),
      oldUid: text(500),
      newUid: text(500),
      newStartUtc: isoDateTime,
      reason: text(1_000).optional(),
      rescheduledAt: isoDateTime,
    }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('booking_cancelled'),
    payload: z.object({
      sessionId: text(200),
      uid: text(500),
      reason: text(1_000),
      cancelledAt: isoDateTime,
    }).strict(),
  }).strict(),
  z.object({
    type: z.literal('event'),
    kind: z.literal('persist_failed'),
    payload: z.object({
      tool: z.enum(['reschedule_briefing', 'cancel_briefing']),
      uid: text(500),
      error: text(),
    }).strict(),
  }).strict(),
]);

export const serverJsonEventSchema = z.union([
  z.object({ type: z.literal('speech_started') }).strict(),
  z.object({ type: z.literal('interrupted') }).strict(),
  z.object({ type: z.literal('turn_complete') }).strict(),
  z.object({ type: z.literal('identity_accepted') }).strict(),
  z.object({ type: z.literal('error'), message: text(500) }).strict(),
  serverUiEventSchema,
]);

export type ServerJsonEvent = z.infer<typeof serverJsonEventSchema>;
export type ServerEventKind = z.infer<typeof serverUiEventSchema>['kind'];
export type WorkflowDiagramPayload = z.infer<typeof workflowDiagramPayloadSchema>;
export type WorkflowDiagramNode = z.infer<typeof workflowDiagramNodeSchema>;

export function parseServerJsonEvent(value: unknown): ServerJsonEvent | undefined {
  const parsed = serverJsonEventSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}
